import { VertexData as BABYLON_VertexData } from '@inter-ikea-betrakta-babylonjs/core/Meshes/mesh.vertexData.js';
import { Matrix as BABYLON_Matrix, Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Mesh as BABYLON_Mesh } from '@inter-ikea-betrakta-babylonjs/core/Meshes/mesh.js';
import { CSG as BABYLON_CSG } from '@inter-ikea-betrakta-babylonjs/core/Meshes/csg.js';
import { MeshBuilder as BABYLON_MeshBuilder } from '@inter-ikea-betrakta-babylonjs/core/Meshes/meshBuilder.js';
import { IVector3 } from '../api.js';
import { EngineStore as BABYLON_EngineStore } from '@inter-ikea-betrakta-babylonjs/core/Engines/engineStore.js';
import { PARALLEL_DOT, matrix_pool, vector3_pool } from '../Math.js';
import { build_bvh_from_mesh } from './Raycast.js';
export class CutoutShape {
    constructor(vertices, basis_x = { x: 1, y: 0, z: 0 }, basis_y = { x: 0, y: 0, z: 1 }) {
        var _a;
        if (vertices.length <= 2) {
            throw new Error('Not enough vertices provided for cutout shape');
        }
        const basis_x_length = IVector3.magnitude(basis_x);
        const basis_y_length = IVector3.magnitude(basis_y);
        if (basis_x_length === 0.0 || basis_y_length === 0.0) {
            throw new Error('One or both of basis vectors are zero-vectors');
        }
        if (Math.abs(IVector3.dot(basis_x, basis_y) / (basis_x_length * basis_y_length)) >= PARALLEL_DOT) {
            throw new Error('Basis vectors are parallel');
        }
        // We do not allow these to be set by the user as we consider the cut to be done in 2D
        const extent_low = 100000.0;
        const extent_high = -100000.0;
        this.basis_x = { x: basis_x.x, y: basis_x.y, z: basis_x.z };
        this.basis_y = { x: basis_y.x, y: basis_y.y, z: basis_y.z };
        const vertices_copy = [];
        for (let i = 0; i < vertices.length; i++) {
            vertices_copy.push({
                x: vertices[i].x,
                y: vertices[i].y
            });
        }
        this.vertices = vertices_copy;
        const is_closed = vertices[0].x === vertices[vertices.length - 1].x && vertices[0].y === vertices[vertices.length - 1].y;
        const shape = [];
        shape.length = vertices.length;
        const tmp_x = vector3_pool.fetch();
        const tmp_y = vector3_pool.fetch();
        const axis_x = vector3_pool.fetch().copyFromFloats(basis_x.x, basis_x.y, basis_x.z);
        const axis_y = vector3_pool.fetch().copyFromFloats(basis_y.x, basis_y.y, basis_y.z);
        const axis_z = BABYLON_Vector3.CrossToRef(axis_y, axis_x, vector3_pool.fetch());
        // In-place normalize. No point for our z-vector to not be normalized.
        axis_z.normalize();
        this.basis_z = { x: axis_z.x, y: axis_z.y, z: axis_z.z };
        for (let i = 0; i < shape.length; i++) {
            shape[i] = new BABYLON_Vector3(vertices[i].x, vertices[i].y, 0);
        }
        // These are not vector-pooled because we don't know what Babylon does with the references, hence we create new vectors for all data we pipe to Babylon.
        const z_low = new BABYLON_Vector3(0, extent_low, 0);
        const z_high = new BABYLON_Vector3(0, extent_high, 0);
        this.mesh = BABYLON_MeshBuilder.ExtrudeShape('a', {
            shape: shape,
            path: [z_low, z_high],
            closeShape: !is_closed,
            sideOrientation: BABYLON_Mesh.BACKSIDE
        });
        const transform = matrix_pool.fetch();
        // Very funky and unintuitive transform to align it with our basis vectors and be right-handed.
        // Babylon must be doing some strange things in the ExtrudeShape function, but this is tested to work.
        transform.setRowFromFloats(0, -axis_y.x, -axis_y.y, -axis_y.z, 0.0);
        transform.setRowFromFloats(1, axis_z.x, axis_z.y, axis_z.z, 0.0);
        transform.setRowFromFloats(2, axis_x.x, axis_x.y, axis_x.z, 0.0);
        transform.setRowFromFloats(3, 0.0, 0.0, 0.0, 1.0);
        this.mesh.bakeTransformIntoVertices(transform);
        vector3_pool.return(tmp_x, tmp_y, axis_x, axis_y, axis_z);
        matrix_pool.return(transform);
        // This is how Babylon forcefully adds the mesh to our scene even when we don't give it a scene. So this is how we remove it as well.
        (_a = BABYLON_EngineStore.LastCreatedScene) === null || _a === void 0 ? void 0 : _a.removeMesh(this.mesh);
    }
    clone() {
        return new CutoutShape(this.vertices, this.basis_x, this.basis_y);
    }
    dispose() {
        this.mesh.dispose();
    }
}
export class CutoutObject {
    constructor(viewer, source_object) {
        this._cached_cut_object_meshes_csg = null;
        // A queue to put cutout shapes that couldn't cut immediately as the object hadn't loaded yet.
        this._async_cut_queue = [];
        this._is_ready = false;
        if (source_object.is_empty) {
            throw new Error('Tried to create CutoutObject from empty source object!');
        }
        this._viewer = viewer;
        this._source_object = source_object;
        this._cut_object = this._viewer.viewer_scene.objects.get(this._viewer.clone_object(source_object.id));
        // For backwards-compatibility, we need to set the object to be enabled & misc,
        // as we've instructed people to disable their source objects and now we use clone for simplicity.
        // Previously we created new objects.
        this._cut_object.enabled = true;
        this._cut_object.visible = true;
        const original_ready = this._cut_object.ready;
        this._cut_object.ready = new Promise((resolve, reject) => {
            original_ready.then(() => {
                this._is_ready = true;
                this._reset();
                this.cut(this._async_cut_queue);
                this.clear_async_queue();
                resolve();
            });
            original_ready.catch(reject);
        });
    }
    /*
      This internal function is for us to be able to reset the cutout mesh on load,
      which happens after source object is ready, but before its is_loaded flag has been set
    */
    _reset() {
        var _a;
        if (!this._is_ready) {
            throw new Error("Cutout object _reset was called on a non-loaded object. This is an internal error and shouldn't happen");
        }
        (_a = this._cut_object._asset_instance) === null || _a === void 0 ? void 0 : _a.instance_iterator((instance) => {
            instance.unload_unique_vertex_source_data();
            this._cut_object.recalculate_local_bounds();
            instance.metadata.custom_raycast_acceleration_structure = null;
        });
        this._cached_cut_object_meshes_csg = null;
    }
    reset() {
        if (!this._is_ready) {
            // Resetting before we have loaded is equivalent to just not cutting anything when we do load
            this.clear_async_queue();
        }
        else {
            this._reset();
        }
    }
    get_cut_object() {
        return this._cut_object.id;
    }
    cut(shapes) {
        var _a, _b, _c;
        if (!this._is_ready) {
            // If we haven't loaded, we put clones of the shapes on the queue
            for (let i = 0; i < shapes.length; i++) {
                this._async_cut_queue.push(shapes[i].clone());
            }
            return;
        }
        if (shapes.length === 0)
            return;
        const cut_meshes = [];
        const cut_instances = [];
        (_a = this._cut_object._asset_instance) === null || _a === void 0 ? void 0 : _a.instance_iterator((instance) => {
            const m = instance.load_unique_vertex_source_data();
            cut_meshes.push(m);
            cut_instances.push(instance.babylon_instance);
        });
        if (cut_meshes.length === 0) {
            throw new Error("Can't cut cutout object - the cutout object has no meshes");
        }
        if (this._cached_cut_object_meshes_csg === null || this._cached_cut_object_meshes_csg.length !== cut_meshes.length) {
            // We don't have any cached CSGs for our cut object, so we need to load them
            this._cached_cut_object_meshes_csg = [];
            for (let i = 0; i < cut_meshes.length; i++) {
                const mesh = cut_meshes[i];
                cut_instances[i].getWorldMatrix().decomposeToTransformNode(mesh);
                const csg = BABYLON_CSG.FromMesh(mesh);
                // We need to inverse the CSG, as we use right-handedness and Babylon's CSG library is fixed to left-handedness.
                csg.inverseInPlace();
                this._cached_cut_object_meshes_csg.push(csg);
            }
        }
        const cutting_csgs = [];
        for (let i = 0; i < shapes.length; i++) {
            const cutting_mesh = shapes[i].mesh;
            cutting_mesh.position = this._cut_object.get_position();
            cutting_mesh.rotation = this._cut_object.get_rotation();
            cutting_mesh.scaling = this._cut_object.get_scale();
            const csg = BABYLON_CSG.FromMesh(cutting_mesh, true);
            // We need to inverse the CSG, as we use right-handedness and Babylon's CSG library is fixed to left-handedness.
            csg.inverseInPlace();
            cutting_csgs.push(csg);
        }
        for (let i = 0; i < cut_meshes.length; i++) {
            const world_matrix = cut_instances[i].getWorldMatrix();
            const mesh = cut_meshes[i];
            const source_csg = this._cached_cut_object_meshes_csg[i];
            for (let i = 0; i < cutting_csgs.length; i++) {
                source_csg.subtractInPlace(cutting_csgs[i]);
            }
            const new_mesh = source_csg.toMesh('aaaaaa', undefined, undefined, false);
            new_mesh.setIndices(new_mesh.getIndices(true, true).reverse());
            const vertex_data = BABYLON_VertexData.ExtractFromMesh(new_mesh);
            for (const kind of mesh.getVerticesDataKinds()) {
                mesh.removeVerticesData(kind);
            }
            if (((_b = vertex_data.positions) === null || _b === void 0 ? void 0 : _b.length) === 0) {
                return;
            }
            if (vertex_data.normals) {
                // Babylon doesn't play nice with CSG and transforms so we need to recalculate our normals back to local space
                // We also flip the sign of the normal, as we've reversed the indices (due to our right-handedness and Babylon's left-handedness)
                const wm = world_matrix.invertToRef(new BABYLON_Matrix());
                const tmp = new BABYLON_Vector3();
                const tmp2 = new BABYLON_Vector3();
                for (let i = 0; i < vertex_data.normals.length; i += 3) {
                    tmp.x = -vertex_data.normals[i + 0];
                    tmp.y = -vertex_data.normals[i + 1];
                    tmp.z = -vertex_data.normals[i + 2];
                    tmp.normalize();
                    BABYLON_Vector3.TransformNormalToRef(tmp, wm, tmp2);
                    vertex_data.normals[i + 0] = tmp2.x;
                    vertex_data.normals[i + 1] = tmp2.y;
                    vertex_data.normals[i + 2] = tmp2.z;
                }
            }
            if (vertex_data.tangents) {
                // Same as with our normals, we need to transform them back to local space.
                const wm = world_matrix.invertToRef(new BABYLON_Matrix());
                const tmp = new BABYLON_Vector3();
                const tmp2 = new BABYLON_Vector3();
                for (let i = 0; i < vertex_data.tangents.length; i += 4) {
                    tmp.x = -vertex_data.tangents[i + 0];
                    tmp.y = -vertex_data.tangents[i + 1];
                    tmp.z = -vertex_data.tangents[i + 2];
                    tmp.normalize();
                    BABYLON_Vector3.TransformNormalToRef(tmp, wm, tmp2);
                    vertex_data.tangents[i + 0] = tmp2.x;
                    vertex_data.tangents[i + 1] = tmp2.y;
                    vertex_data.tangents[i + 2] = tmp2.z;
                }
            }
            vertex_data.applyToMesh(mesh);
            new_mesh.dispose();
        }
        this._cut_object.recalculate_local_bounds();
        // Since raycasting is based on the original source mesh, we override it here with new data so that we can raycast through the hole
        (_c = this._cut_object._asset_instance) === null || _c === void 0 ? void 0 : _c.instance_iterator((instance) => {
            instance.metadata.custom_raycast_acceleration_structure = build_bvh_from_mesh(instance.babylon_instance);
        });
        this._viewer.mark_scene_dirty();
    }
    dispose(dispose_source_object, dispose_cut_object) {
        if (dispose_source_object && this._viewer.viewer_scene.objects.contains(this._source_object.id)) {
            this._viewer.delete_object(this._source_object.id);
        }
        if (dispose_cut_object && this._viewer.viewer_scene.objects.contains(this._cut_object.id)) {
            this._viewer.delete_object(this._cut_object.id);
        }
        this._cached_cut_object_meshes_csg = null;
    }
    // Disposes all elements in the async cut queue and empties the array
    clear_async_queue() {
        for (let i = 0; i < this._async_cut_queue.length; i++) {
            this._async_cut_queue[i].dispose();
        }
        this._async_cut_queue.length = 0;
    }
}
//# sourceMappingURL=CutoutObject.js.map